import React from "react";
import { ArrowRight, Droplet, ArrowDownUp, Layers3, BellRing } from "lucide-react";
import { PRODUCTS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

const items = [
  {
    slug: "trigger",
    tag: "01 / Municipal",
    icon: Droplet,
    title: "Trigger",
    subtitle: "Never miss the water supply",
    features: [
      "Smart controller for corporation / municipal water line",
      "Auto-starts motor the moment supply arrives",
      "Dry-run protection when line is empty",
      "Real-time SMS + app alerts",
    ],
    price: "Get a Quote",
  },
  {
    slug: "macro",
    tag: "02 / Borewell",
    icon: ArrowDownUp,
    title: "Macro",
    subtitle: "Prevents tank overflow",
    features: [
      "For borewell / submersible pumps (UG → OH tank)",
      "Wireless level sensing on both tanks",
      "Auto ON/OFF with overflow cutoff",
      "Web + Android + iOS remote control",
    ],
    price: "Get a Quote",
  },
  {
    slug: "matrix",
    tag: "03 / Industrial",
    icon: Layers3,
    title: "Matrix",
    subtitle: "Multi-tank water level controller",
    features: [
      "Handles multiple tanks and pumps in one system",
      "Ideal for hospitals, malls, factories & campuses",
      "Smart scheduling & duty rotation logic",
      "SCADA / BMS-friendly integration",
    ],
    price: "Enterprise pricing",
  },
  {
    slug: "flexibell",
    tag: "04 / Alarm",
    icon: BellRing,
    title: "Flexibell",
    subtitle: "Wireless water level alarm",
    features: [
      "Standalone wireless level alarm",
      "Distinct alerts for low / full / overflow",
      "Battery-friendly wireless sensor",
      "Perfect for large industrial reservoirs",
    ],
    price: "Ask for a Demo",
  },
];

export default function Products() {
  return (
    <section id="products" data-testid={PRODUCTS.root} className="border-b border-zinc-200">
      <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#002FA7] mb-4">
            /// Product Range
          </div>
          <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">
            Four products. One rock-solid platform.
          </h2>
          <p className="mt-5 text-zinc-700 text-lg">
            Every Aquamatic controller runs on the same wireless mesh &amp; safety logic — sized
            for your building, calibrated for your water source.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.slug}
                data-testid={PRODUCTS.card(p.slug)}
                className="group border border-zinc-200 bg-white p-8 shadow-hard transition-all duration-300 hover:-translate-y-1 flex flex-col"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {p.tag}
                  </span>
                  <div className="w-11 h-11 border border-zinc-300 flex items-center justify-center group-hover:bg-[#002FA7] group-hover:text-white group-hover:border-[#002FA7] transition-colors">
                    <Icon size={20} />
                  </div>
                </div>

                <h3 className="text-2xl font-semibold tracking-tight">{p.title}</h3>
                <p className="text-zinc-600 mt-1">{p.subtitle}</p>

                <ul className="mt-6 space-y-2.5 text-sm text-zinc-700 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-3">
                      <span className="mt-2 w-1.5 h-1.5 bg-[#FF5F1F] shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-zinc-200 flex items-center justify-between">
                  <div className="font-mono text-xs uppercase tracking-widest text-zinc-800">
                    {p.price}
                  </div>
                  <a href="#contact">
                    <Button
                      data-testid={PRODUCTS.enquire(p.slug)}
                      variant="ghost"
                      className="rounded-none font-mono uppercase text-xs tracking-wider text-[#002FA7] hover:text-white hover:bg-[#002FA7] px-4"
                    >
                      Enquire <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
